import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AuthService,
  CurbNTurfCashService,
  DonationService,
  IPaymentMethod,
  PaymentMethodsService,
  ToastService,
} from '@cnt-workspace/data-access';
import { Subscription, firstValueFrom } from 'rxjs';
import { FocusTrapDirective } from '../directives/focus-trap.directive';
import { CCNumberFormatDirective } from '@curbnturf/cnt-masks/src/lib/cc/directives/cc-number-format.directive';
import { CCCvcFormatDirective } from '@curbnturf/cnt-masks/src/lib/cc/directives/cc-cvc-format.directive';

function extractErrorMessage(err: unknown): string | null {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { message?: string | string[] } | undefined;
    if (Array.isArray(body?.message)) return body.message[0] ?? null;
    if (typeof body?.message === 'string') return body.message;
    return null;
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

/**
 * Tip a boondocking site or POI caretaker. Applies the guest's CurbNTurf Cash balance first
 * (server-authoritative, no toggle) and only requires a real (Converge-tokenized) card on file
 * for whatever's left — same add-card sub-flow as `BookingReviewComponent`'s real-card form
 * (`PaymentMethodsService.addRealCard`/`hydrateRealCard`) — since the donation endpoints charge
 * whatever card the backend has for the signed-in user, not a token passed in the request body.
 */
@Component({
  selector: 'cnt-donate-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, FocusTrapDirective, CCNumberFormatDirective, CCCvcFormatDirective],
  templateUrl: './donate-modal.component.html',
})
export class DonateModalComponent implements OnChanges, OnDestroy {
  private platformId = inject<object>(PLATFORM_ID);
  private auth = inject(AuthService);
  private payments = inject(PaymentMethodsService);
  private donationSvc = inject(DonationService);
  private cash = inject(CurbNTurfCashService);
  private toasts = inject(ToastService);

  @Input() open = false;
  @Input() targetType: 'boondock' | 'poi' | null = null;
  @Input() targetId: number | null = null;
  @Input() targetName = '';
  @Output() closed = new EventEmitter<void>();

  @ViewChild('amountInput') amountInput?: ElementRef<HTMLInputElement>;

  amount: number | null = 5;
  customAmount = false;
  paymentMethods: IPaymentMethod[] = [];
  availableCash = 0;
  submitting = false;
  error: string | null = null;
  private methodsSub: Subscription | null = null;
  private cashSub: Subscription | null = null;

  /** Inline add-card form (mirrors BookingReviewComponent's real-card fields). */
  cardNumber = '';
  cardExpMonth = '';
  cardExpYear = '';
  cardCvv = '';
  cardAddress = '';
  cardCity = '';
  cardState = '';
  cardZip = '';
  savingCard = false;
  cardError: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open']) return;
    if (this.open) {
      this.resetForm();
      this.methodsSub = this.payments.methods$.subscribe((methods) => (this.paymentMethods = methods));
      const userId = this.auth.currentUser?.id;
      if (userId) {
        void this.payments.hydrateRealCard(userId);
        this.refreshAvailableCash(userId);
      }
      if (isPlatformBrowser(this.platformId)) document.body.classList.add('cnt-modal-open');
    } else {
      this.methodsSub?.unsubscribe();
      this.methodsSub = null;
      this.cashSub?.unsubscribe();
      this.cashSub = null;
      if (isPlatformBrowser(this.platformId)) document.body.classList.remove('cnt-modal-open');
    }
  }

  ngOnDestroy(): void {
    this.methodsSub?.unsubscribe();
    this.cashSub?.unsubscribe();
    if (isPlatformBrowser(this.platformId)) document.body.classList.remove('cnt-modal-open');
  }

  private refreshAvailableCash(userId: number): void {
    this.cashSub?.unsubscribe();
    this.cashSub = this.cash.getAvailableCredit(userId).subscribe((v) => (this.availableCash = v));
  }

  get hasRealCard(): boolean {
    return this.paymentMethods.some((m) => m.isReal);
  }

  /** Portion of the current amount CurbNTurf Cash covers — applied automatically server-side,
   *  same "no guest toggle" contract BookingReviewComponent's real-listing branch uses. */
  get cashPortion(): number {
    return Math.min(this.availableCash, this.amount ?? 0);
  }

  /** Portion that still needs a card, after CurbNTurf Cash is applied. */
  get cardPortion(): number {
    return Math.max(0, (this.amount ?? 0) - this.availableCash);
  }

  get needsCard(): boolean {
    return this.cardPortion > 0;
  }

  /** Real-card form validity — full card number (13-19 digits), MM/YYYY, CVV, and
   *  billing address (the server requires one on file to request a token). */
  get canAddCard(): boolean {
    const digits = this.cardNumber.replace(/\s/g, '');
    return (
      digits.length >= 13 &&
      digits.length <= 19 &&
      /^\d{2}$/.test(this.cardExpMonth) &&
      /^\d{4}$/.test(this.cardExpYear) &&
      /^\d{3,4}$/.test(this.cardCvv) &&
      !!this.cardAddress.trim() &&
      !!this.cardCity.trim() &&
      !!this.cardState.trim() &&
      /^\d{5}$/.test(this.cardZip)
    );
  }

  async addCard(): Promise<void> {
    const user = this.auth.currentUser;
    if (!this.canAddCard || !user?.id || this.savingCard) return;
    this.savingCard = true;
    this.cardError = null;
    try {
      await this.payments.addRealCard({
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        address: this.cardAddress.trim(),
        city: this.cardCity.trim(),
        state: this.cardState.trim(),
        zipcode: this.cardZip,
        cardNumber: this.cardNumber,
        expMonth: this.cardExpMonth,
        expYear: this.cardExpYear,
        cvv: this.cardCvv,
      });
      this.toasts.success('Card added.');
      this.cardNumber = '';
      this.cardExpMonth = '';
      this.cardExpYear = '';
      this.cardCvv = '';
      this.cardAddress = '';
      this.cardCity = '';
      this.cardState = '';
      this.cardZip = '';
    } catch (err) {
      this.cardError = 'Could not save that card — check the number and try again.';
      console.error('[donate-modal] addRealCard failed', err);
    } finally {
      this.savingCard = false;
    }
  }

  setAmount(value: number): void {
    if (value < 5) {
      this.customAmount = true;
      this.amount = null;
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.amountInput?.nativeElement.focus());
      }
    } else {
      this.customAmount = false;
      this.amount = value;
    }
  }

  get canDonate(): boolean {
    return (
      !!this.targetType &&
      !!this.targetId &&
      this.amount !== null &&
      this.amount >= 5 &&
      (!this.needsCard || this.hasRealCard) &&
      !this.submitting
    );
  }

  /** paymentMethod is hardcoded — CurbNTurf Cash is applied automatically server-side (no
   *  guest-facing toggle, same contract reservations use), so there's nothing for the guest
   *  to choose here; the card is only ever charged for whatever CNTCash doesn't cover. */
  async donate(): Promise<void> {
    if (!this.canDonate || !this.targetType || !this.targetId || this.amount === null) return;
    this.submitting = true;
    this.error = null;
    let succeeded = false;
    try {
      const res = await firstValueFrom(
        this.targetType === 'boondock'
          ? this.donationSvc.donateToBoondock(this.targetId, this.amount)
          : this.donationSvc.donateToPoi(this.targetId, this.amount),
      );
      if (!res.success) throw new Error(res.message || 'Donation failed.');
      this.toasts.success('Thank you for your donation!');
      succeeded = true;
    } catch (err) {
      this.error = extractErrorMessage(err) || 'Could not process your donation — please try again.';
      console.error('[donate-modal] donate failed', err);
    } finally {
      this.submitting = false;
      // The server may have just partially or fully spent the guest's balance (even on a
      // failed attempt, e.g. CNTCash-applied-then-card-declined) — refresh so a stale, too-high
      // balance doesn't linger in the UI for a retry.
      const userId = this.auth.currentUser?.id;
      if (userId) this.refreshAvailableCash(userId);
    }
    // Emitted after `submitting` resets so `close()`'s in-flight guard doesn't
    // swallow this — a backdrop/close-button click mid-request should still be blocked.
    if (succeeded) this.closed.emit();
  }

  close(): void {
    if (this.submitting) return;
    this.closed.emit();
  }

  private resetForm(): void {
    this.amount = 5;
    this.customAmount = false;
    this.error = null;
    this.submitting = false;
    this.cardNumber = '';
    this.cardExpMonth = '';
    this.cardExpYear = '';
    this.cardCvv = '';
    this.cardAddress = '';
    this.cardCity = '';
    this.cardState = '';
    this.cardZip = '';
    this.cardError = null;
    this.savingCard = false;
  }
}
